import { useQuery } from "@apollo/client";
import { gql } from "generated-graphql";

/**
 * Ranked, linkable state-registration candidates for a single stream (T4).
 * Only candidates the server considers linkable are returned — no
 * zero-shared-code entries — and the array is already sorted best-first, so
 * the Match tab renders it in order without re-sorting client-side.
 *
 * `relatedStream` is the stream a candidate's registration already owns, when it
 * owns one: its presence makes the candidate a merge rather than a link. It
 * carries the grid display fields the merge card shows plus everything the
 * deep-linked merge wizard reads off each row (amounts, the legacy registration
 * triple, isProvisional), so a forwarded target behaves like a real grid row
 * there. Server-sourced rather than scraped from the grid's loaded page, so a
 * target on a later page or hidden by a filter is still offered.
 */
export const WASTE_STREAM_REGISTRATION_MATCHES_QUERY = gql(`
  query WasteStreamRegistrationMatches($wasteStreamId: ID!) {
    wasteStreamRegistrationMatches(wasteStreamId: $wasteStreamId) {
      score
      breakdown {
        code
        dot
        description
        form
        stateCode
      }
      sharedFederalCodes
      extraStreamCodes
      isExact
      isMember
      stream {
        name
        federalWasteCodes
        stateWasteCodes
        dotIds
        formCodes
      }
      registration {
        id
        stateRegisteredId
        status
        state
        name
        federalWasteCodes
        dotId
        dotDescription
        formCodes
      }
      relatedStream {
        id
        name
        isProvisional
        eManifestProfileNumber
        vendorProfileNumber
        federalWasteCodes
        stateWasteCodes
        classification
        formCode
        sourceCode
        vendorAmount
        eManifestAmount
        vendorAmountAllTime
        eManifestAmountAllTime
        comment
        registration {
          id
          stateRegisteredId
          status
          name
        }
      }
    }
  }
`);

export function useWasteStreamRegistrationMatches(wasteStreamId: string, skip: boolean) {
  return useQuery(WASTE_STREAM_REGISTRATION_MATCHES_QUERY, {
    variables: { wasteStreamId },
    skip,
  });
}

/** Manually links a stream (and its raws) to a state registration (T3). Staff-only. */
export const LINK_WASTE_STREAM_TO_REGISTRATION_MUTATION = gql(`
  mutation LinkWasteStreamToRegistration($wasteStreamId: ID!, $registrationId: ID!) {
    linkWasteStreamToRegistration(wasteStreamId: $wasteStreamId, registrationId: $registrationId)
  }
`);
